import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PoweredBy } from "@/components/PoweredBy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PublicLoading, PublicError } from "@/pages/public/VisitorRegister";
import { toast } from "sonner";
import { Loader2, Users, Tv, MapPin, Clock, CheckCircle2, AlertTriangle, CalendarClock } from "lucide-react";

function AvailabilityTimeline({ bookings, openStart = 9, openEnd = 20 }) {
  const total = (openEnd - openStart) * 60;
  const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  return (
    <div>
      <div className="relative h-9 rounded-lg bg-emerald-50 border border-emerald-200 overflow-hidden" data-testid="availability-timeline">
        {bookings.map((b, i) => {
          const s = ((toMin(b.start_time) - openStart * 60) / total) * 100;
          const w = ((toMin(b.end_time) - toMin(b.start_time)) / total) * 100;
          const pending = b.status === "PENDING_APPROVAL";
          return (
            <div key={i} className="absolute top-0 bottom-0 flex items-center justify-center text-[9px] font-semibold text-white overflow-hidden"
              style={{ left: `${Math.max(0, s)}%`, width: `${w}%`, background: pending ? "#F59E0B" : "#16305B" }}
              title={`${b.start_time}-${b.end_time}`}>
              <span className="truncate px-1">{b.start_time}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-mono">
        <span>{String(openStart).padStart(2, "0")}:00</span><span>{String(openEnd).padStart(2, "0")}:00</span>
      </div>
      <div className="flex gap-4 mt-2 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-100 border border-emerald-300" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-navy-900" /> Booked</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-500" /> Pending</span>
      </div>
    </div>
  );
}

export default function RoomBooking() {
  const { publicId } = useParams();
  const [room, setRoom] = useState(null);
  const [err, setErr] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [form, setForm] = useState({ person_name: "", company_name: "", phone: "", person_email: "", cabin_number: "", purpose: "", start_time: "10:00", end_time: "11:00" });
  const [avail, setAvail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const loadRoom = useCallback((d) => {
    axios.get(`${API_BASE}/public/room/${publicId}?date=${d}`).then((r) => setRoom(r.data)).catch((e) => setErr(apiError(e)));
  }, [publicId]);

  useEffect(() => { loadRoom(date); }, [date, loadRoom]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const check = useCallback(async () => {
    if (!form.start_time || !form.end_time) return;
    try {
      const { data } = await axios.get(`${API_BASE}/public/room/${publicId}/availability`, { params: { date, start: form.start_time, end: form.end_time } });
      setAvail(data);
    } catch { setAvail(null); }
  }, [publicId, date, form.start_time, form.end_time]);

  useEffect(() => { check(); }, [check]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/room/${publicId}/book`, { ...form, date });
      setDone(data);
      toast.success(data.message);
      loadRoom(date);
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSubmitting(false); }
  };

  if (err) return <PublicError title="Room Unavailable" message={err} />;
  if (!room) return <PublicLoading />;

  const [oh0, oh1] = (room.booking_hours || "09:00-20:00").split("-");
  const openStart = parseInt(oh0), openEnd = parseInt(oh1);
  const facIcons = { TV: Tv, "Video Conference": Tv };

  if (done) return (
    <div className="min-h-screen sf-grid-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center sf-fade-up">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${done.approval_required ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
          {done.approval_required ? <Clock size={34} /> : <CheckCircle2 size={34} />}
        </div>
        <h2 className="font-display text-2xl font-bold text-navy-900">{done.approval_required ? "Awaiting Approval" : "Booking Confirmed"}</h2>
        <p className="text-slate-500 mt-2">{done.message}</p>
        <div className="mt-6 p-4 rounded-xl bg-slate-50 border text-left text-sm">
          <p className="font-semibold text-navy-900">{room.room_name}</p>
          <p className="text-slate-500">{date} · {form.start_time} – {form.end_time}</p>
        </div>
        <Button className="mt-6 bg-navy-900" onClick={() => { setDone(null); setForm({ ...form, purpose: "" }); }}>Make another booking</Button>
        <div className="mt-6 flex justify-center"><PoweredBy /></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sf-grid-bg px-6 py-8 text-white"><Logo className="h-7" light /></div>
      <div className="max-w-3xl mx-auto px-4 -mt-6 pb-12">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-6 sm:p-8 sf-fade-up">
          {room.logo_url && <CompanyLogo logoUrl={room.logo_url} className="h-9 mb-4" alt={room.company} />}
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 flex items-center gap-1"><MapPin size={14} /> {room.company} · {room.branch}</p>
          <h1 className="font-display text-3xl font-bold text-navy-900 mt-1">{room.room_name}</h1>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="inline-flex items-center gap-1 text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full"><Users size={14} /> {room.capacity} seats</span>
            {room.facilities.map((f) => (
              <span key={f} className="inline-flex items-center gap-1 text-sm text-navy-800 bg-navy-50 px-3 py-1 rounded-full">{f}</span>
            ))}
            <span className="inline-flex items-center gap-1 text-sm text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full"><Clock size={14} /> Free up to {room.free_duration_minutes} min</span>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-navy-900">Availability</h3>
              <Input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="booking-date-top" />
            </div>
            <AvailabilityTimeline bookings={room.bookings} openStart={openStart} openEnd={openEnd} />
            {room.bookings.length > 0 && (
              <div className="mt-3 space-y-1">
                {room.bookings.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <CalendarClock size={14} className="text-slate-400" /> {b.start_time} – {b.end_time}
                    <span className="text-xs text-slate-400">{b.status === "PENDING_APPROVAL" ? "(pending)" : "(booked)"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <h3 className="font-display font-semibold text-navy-900">Book this room</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Your Name *</Label><Input required data-testid="book-name" className="mt-1.5 h-11" value={form.person_name} onChange={(e) => set("person_name", e.target.value)} /></div>
              <div><Label>Company *</Label><Input required data-testid="book-company" className="mt-1.5 h-11" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></div>
              <div><Label>Phone *</Label><Input required data-testid="book-phone" className="mt-1.5 h-11" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              <div><Label>Email *</Label><Input required type="email" data-testid="book-email" className="mt-1.5 h-11" value={form.person_email} onChange={(e) => set("person_email", e.target.value)} /></div>
              <div><Label>Cabin Number *</Label><Input required data-testid="book-cabin" className="mt-1.5 h-11" value={form.cabin_number} onChange={(e) => set("cabin_number", e.target.value)} /></div>
              <div><Label>Date *</Label><Input required type="date" min={today} data-testid="book-date" className="mt-1.5 h-11" value={date} onChange={(e) => setDate(e.target.value)} /></div>
              <div><Label>Start Time *</Label><Input required type="time" data-testid="book-start" className="mt-1.5 h-11" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} /></div>
              <div><Label>End Time *</Label><Input required type="time" data-testid="book-end" className="mt-1.5 h-11" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} /></div>
            </div>
            <div><Label>Purpose *</Label><Textarea required data-testid="book-purpose" className="mt-1.5" value={form.purpose} onChange={(e) => set("purpose", e.target.value)} /></div>

            {avail && (
              <div className={`p-3 rounded-xl border text-sm flex items-center gap-2 ${avail.available ? (avail.approval_required ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700") : "bg-rose-50 border-rose-200 text-rose-700"}`} data-testid="availability-status">
                {avail.available ? (avail.approval_required ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />) : <AlertTriangle size={16} />}
                {!avail.available ? "This slot overlaps an existing booking. Please choose another time."
                  : avail.approval_required ? `Available. Duration ${avail.duration_minutes} min exceeds free ${avail.free_duration_minutes} min — needs Branch Admin approval.`
                  : `Available! Duration ${avail.duration_minutes} min — auto-confirms.`}
              </div>
            )}

            <Button type="submit" disabled={submitting || (avail && !avail.available)} data-testid="book-submit" className="w-full h-12 bg-navy-900 hover:bg-navy-800 font-semibold text-base">
              {submitting ? <Loader2 className="animate-spin" /> : "Request Booking"}
            </Button>
          </form>
          <div className="mt-6 pt-4 border-t border-slate-100 flex justify-center"><PoweredBy /></div>
        </div>
      </div>
    </div>
  );
}
